import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { StatusBar, Style } from '@capacitor/status-bar';
import { isNativeApp, triggerHaptic, allowLandscape, lockPortrait, setStatusBarTheme } from '../utils/nativeBridge';
import { getMediaUrl } from '../utils/mediaUrl';
import { videoSources } from '../utils/videoSources';
import { useToast } from '../contexts/ToastContext';

/**
 * NativeImageViewer
 *
 * Edge-to-edge native media lightbox matching iOS Photos / X / Threads:
 * - Dynamic iOS/Android status bar contrast (white text on black modal background)
 * - True edge-to-edge framing (w-screen object-contain rounded-none)
 * - Close button on Top-Left, image counter only when > 1, options menu on Top-Right
 * - Sleek bottom engagement overlay with Reply, Repost, Like, and Share buttons
 * - Vertical swipe-to-dismiss with dynamic backdrop opacity and spring physics
 * - Horizontal swipe navigation between multiple images
 * - Single-tap to toggle immersive chrome
 * - Double-tap to zoom (1x <-> 2.5x) at tap coordinates
 */
export default function NativeImageViewer({
  media = [],
  initialIndex = 0,
  onClose,
  post,
  liked = false,
  likesCount = 0,
  onLike,
  reBazzed = false,
  reBazzesCount = 0,
  onReBazz,
  repliesCount = 0,
  onReply,
  onShare,
}) {
  const toast = useToast();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [showChrome, setShowChrome] = useState(true);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);

  // Zoom & Pan State
  const [scale, setScale] = useState(1);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });

  // Touch & Drag Gesture State
  const [dragY, setDragY] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const gestureModeRef = useRef(null); // 'vertical' | 'horizontal' | null

  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const lastTapTimeRef = useRef(0);
  const singleTapTimerRef = useRef(null);

  // Normalize media items to { url, type }
  const mediaList = (media || []).map((item) => {
    if (typeof item === 'string') {
      const isVid = /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(item);
      return { url: item, type: isVid ? 'video' : 'image' };
    }
    return {
      url: item.url,
      type: item.type || (/\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(item.url) ? 'video' : 'image'),
    };
  });

  const currentMedia = mediaList[currentIndex] || mediaList[0];

  // 1. Switch iOS/Android Status Bar to Light Text (Style.Dark) against Black Backdrop
  useEffect(() => {
    if (isNativeApp()) {
      StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
      try {
        StatusBar.setBackgroundColor({ color: '#000000' }).catch(() => {});
      } catch (_) {}
    }

    // Allow landscape orientation while inspecting photos
    allowLandscape();

    return () => {
      // Revert status bar theme based on user/system theme
      const isDark =
        document.documentElement.classList.contains('dark') ||
        (typeof window !== 'undefined' &&
          window.matchMedia &&
          window.matchMedia('(prefers-color-scheme: dark)').matches);
      setStatusBarTheme(isDark);
      lockPortrait();
    };
  }, []);

  // Keyboard navigation (Esc to close, Left/Right arrows)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setCurrentIndex((prev) => prev - 1);
        setScale(1);
      } else if (e.key === 'ArrowRight' && currentIndex < mediaList.length - 1) {
        setCurrentIndex((prev) => prev + 1);
        setScale(1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, mediaList.length, onClose]);

  // Reset zoom and gestures when switching images
  useEffect(() => {
    setScale(1);
    setDragX(0);
    setDragY(0);
    setIsDragging(false);
    gestureModeRef.current = null;
  }, [currentIndex]);

  // 2. Single-Tap (Toggle Chrome) & Double-Tap (Zoom 2.5x) Handler
  const handleMediaTap = (e) => {
    // If video controls clicked, ignore
    if (e.target.tagName === 'VIDEO') return;

    const now = Date.now();
    const timeDiff = now - lastTapTimeRef.current;

    if (timeDiff < 280 && timeDiff > 0) {
      // DOUBLE TAP: Toggle between 1x and 2.5x zoom
      if (singleTapTimerRef.current) {
        clearTimeout(singleTapTimerRef.current);
        singleTapTimerRef.current = null;
      }

      if (scale > 1) {
        setScale(1);
        setShowChrome(true);
      } else {
        const rect = e.currentTarget.getBoundingClientRect();
        const clientX = e.clientX ?? (e.touches && e.touches[0] ? e.touches[0].clientX : rect.width / 2);
        const clientY = e.clientY ?? (e.touches && e.touches[0] ? e.touches[0].clientY : rect.height / 2);
        const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
        const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
        setOrigin({ x, y });
        setScale(2.5);
        setShowChrome(false); // Auto-hide chrome when zoomed in
        triggerHaptic('light');
      }
    } else {
      // SINGLE TAP: Wait 280ms to confirm it's not a double-tap, then toggle chrome
      singleTapTimerRef.current = setTimeout(() => {
        if (scale > 1) {
          setScale(1);
          setShowChrome(true);
        } else {
          setShowChrome((prev) => !prev);
        }
      }, 280);
    }

    lastTapTimeRef.current = now;
  };

  // 3. Touch Gestures: Vertical Swipe-to-Dismiss and Horizontal Multi-image Navigation
  const handleTouchStart = (e) => {
    if (scale > 1) return;
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
    setIsDragging(true);
    gestureModeRef.current = null;
  };

  const handleTouchMove = (e) => {
    if (!isDragging || scale > 1) return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;

    // Detect primary swipe orientation
    if (!gestureModeRef.current) {
      if (Math.abs(deltaY) > 8 && Math.abs(deltaY) >= Math.abs(deltaX)) {
        gestureModeRef.current = 'vertical';
      } else if (Math.abs(deltaX) > 8 && Math.abs(deltaX) > Math.abs(deltaY) && mediaList.length > 1) {
        gestureModeRef.current = 'horizontal';
      }
    }

    if (gestureModeRef.current === 'vertical') {
      if (e.cancelable) e.preventDefault();
      setDragY(deltaY);
    } else if (gestureModeRef.current === 'horizontal') {
      if (e.cancelable) e.preventDefault();
      const isAtLeft = currentIndex === 0 && deltaX > 0;
      const isAtRight = currentIndex === mediaList.length - 1 && deltaX < 0;
      const resistance = isAtLeft || isAtRight ? 0.3 : 1;
      setDragX(deltaX * resistance);
    }
  };

  const handleTouchEnd = (e) => {
    if (!isDragging) return;
    setIsDragging(false);

    const touch = e.changedTouches ? e.changedTouches[0] : null;
    const deltaX = touch ? touch.clientX - touchStartRef.current.x : dragX;
    const deltaY = touch ? touch.clientY - touchStartRef.current.y : dragY;
    const duration = Date.now() - touchStartRef.current.time;
    const velocityY = Math.abs(deltaY) / (duration || 1);
    const velocityX = Math.abs(deltaX) / (duration || 1);

    if (gestureModeRef.current === 'vertical') {
      // Flicked down or up past 110px threshold or high velocity
      if (Math.abs(deltaY) > 110 || velocityY > 0.5) {
        triggerHaptic('light');
        onClose();
        return;
      }
    } else if (gestureModeRef.current === 'horizontal') {
      // Swiped horizontally to switch images
      if ((deltaX < -50 || velocityX > 0.35) && currentIndex < mediaList.length - 1) {
        triggerHaptic('light');
        setCurrentIndex((prev) => prev + 1);
      } else if ((deltaX > 50 || velocityX > 0.35) && currentIndex > 0) {
        triggerHaptic('light');
        setCurrentIndex((prev) => prev - 1);
      }
    }

    setDragY(0);
    setDragX(0);
    gestureModeRef.current = null;
  };

  // Dynamic background opacity & image scale during vertical dismiss gesture
  const backdropOpacity = Math.max(1 - Math.abs(dragY) / 380, 0.15);
  const dragScale = Math.max(1 - Math.abs(dragY) / 1100, 0.86);

  const handleCopyLink = () => {
    if (!currentMedia?.url) return;
    const fullUrl = getMediaUrl(currentMedia.url);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(fullUrl);
      toast.success('Image link copied to clipboard');
    }
    setShowOptionsMenu(false);
  };

  const handleOpenOriginal = () => {
    if (!currentMedia?.url) return;
    window.open(getMediaUrl(currentMedia.url), '_blank', 'noopener,noreferrer');
    setShowOptionsMenu(false);
  };

  const handleRotateToggle = () => {
    triggerHaptic('light');
    const isLandscape = window.innerWidth > window.innerHeight;
    if (isLandscape) {
      lockPortrait();
    } else {
      allowLandscape();
    }
    setShowOptionsMenu(false);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col justify-between select-none overflow-hidden transition-colors duration-150"
      style={{ backgroundColor: `rgba(0, 0, 0, ${dragY !== 0 ? backdropOpacity : 0.98})` }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* TOP HEADER: Safe-Area Aware, ✕ on Left, Counter only if > 1, ••• on Right */}
      <header
        className={`fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-3 bg-gradient-to-b from-black/85 via-black/45 to-transparent transition-opacity duration-200 ${
          showChrome && dragY === 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Top-Left Close Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            triggerHaptic('light');
            onClose();
          }}
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/35 backdrop-blur-md flex items-center justify-center text-white active:scale-90 transition-transform shadow-lg"
          aria-label="Close viewer"
          title="Close (Esc)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Counter Badge: ONLY shown when images.length > 1 */}
        {mediaList.length > 1 ? (
          <span className="px-3.5 py-1 rounded-full bg-white/15 backdrop-blur-md text-white text-[13px] font-semibold tracking-wide shadow-md">
            {currentIndex + 1} / {mediaList.length}
          </span>
        ) : (
          <div />
        )}

        {/* Top-Right More Options Button */}
        <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              triggerHaptic('light');
              setShowOptionsMenu((prev) => !prev);
            }}
            className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/35 backdrop-blur-md flex items-center justify-center text-white active:scale-90 transition-transform shadow-lg"
            aria-label="More options"
          >
            <i className="fas fa-ellipsis text-base" />
          </button>

          {/* Options Dropdown */}
          {showOptionsMenu && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowOptionsMenu(false)}
              />
              <div
                className="absolute right-0 top-12 z-50 w-52 rounded-2xl bg-zinc-900/95 border border-zinc-800 backdrop-blur-xl shadow-2xl py-1 text-sm text-zinc-100 animate-in fade-in zoom-in-95 duration-150"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={handleOpenOriginal}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/10 text-left transition-colors"
                >
                  <i className="fas fa-arrow-up-right-from-square text-xs text-zinc-400" />
                  <span>Open original</span>
                </button>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/10 text-left transition-colors"
                >
                  <i className="fas fa-link text-xs text-zinc-400" />
                  <span>Copy image link</span>
                </button>
                <button
                  type="button"
                  onClick={handleRotateToggle}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/10 text-left transition-colors"
                >
                  <i className="fas fa-rotate text-xs text-zinc-400" />
                  <span>Toggle landscape</span>
                </button>
                {onShare && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowOptionsMenu(false);
                      onShare();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/10 text-left transition-colors"
                  >
                    <i className="fas fa-share-nodes text-xs text-zinc-400" />
                    <span>Share post</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      {/* DESKTOP / ACCESSIBILITY PREVIOUS ARROW */}
      {mediaList.length > 1 && currentIndex > 0 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            triggerHaptic('light');
            setCurrentIndex((prev) => prev - 1);
          }}
          className={`hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white items-center justify-center transition-all z-20 active:scale-95 ${
            showChrome ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-label="Previous media"
        >
          <i className="fas fa-chevron-left text-base" />
        </button>
      )}

      {/* EDGE-TO-EDGE MEDIA CONTAINER */}
      <main
        onClick={handleMediaTap}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className="flex-1 flex items-center justify-center w-screen h-full overflow-hidden cursor-pointer"
      >
        {currentMedia?.type === 'video' ? (
          <div
            style={{
              transform: `translate3d(${dragX}px, ${dragY}px, 0) scale(${dragScale})`,
              transition: isDragging ? 'none' : 'transform 220ms cubic-bezier(0.2, 0, 0, 1)',
            }}
            className="w-screen max-h-dvh flex items-center justify-center"
          >
            <video
              key={currentMedia.url}
              controls
              autoPlay
              playsInline
              className="w-screen max-h-dvh object-contain rounded-none shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {videoSources(currentMedia.url)}
            </video>
          </div>
        ) : (
          <img
            src={getMediaUrl(currentMedia?.url)}
            alt="Fullscreen media"
            draggable={false}
            style={{
              transformOrigin: `${origin.x}% ${origin.y}%`,
              transform: `translate3d(${dragX}px, ${dragY}px, 0) scale(${
                scale > 1 ? scale : dragScale
              })`,
              transition: isDragging ? 'none' : 'transform 220ms cubic-bezier(0.2, 0, 0, 1)',
            }}
            className="w-screen max-h-dvh object-contain rounded-none select-none pointer-events-none"
          />
        )}
      </main>

      {/* DESKTOP / ACCESSIBILITY NEXT ARROW */}
      {mediaList.length > 1 && currentIndex < mediaList.length - 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            triggerHaptic('light');
            setCurrentIndex((prev) => prev + 1);
          }}
          className={`hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 hover:bg-white/25 text-white items-center justify-center transition-all z-20 active:scale-95 ${
            showChrome ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          aria-label="Next media"
        >
          <i className="fas fa-chevron-right text-base" />
        </button>
      )}

      {/* BOTTOM PAGINATION DOTS (when multiple items) */}
      {mediaList.length > 1 && (
        <div
          className={`absolute bottom-20 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/15 transition-opacity duration-200 ${
            showChrome && dragY === 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          {mediaList.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                triggerHaptic('selection');
                setCurrentIndex(idx);
              }}
              className={`transition-all rounded-full ${
                idx === currentIndex
                  ? 'w-5 h-1.5 bg-white'
                  : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/70'
              }`}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>
      )}

      {/* BOTTOM ENGAGEMENT OVERLAY (Replaces Empty Black Void) */}
      <footer
        className={`fixed bottom-0 inset-x-0 z-30 px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] bg-gradient-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-200 ${
          showChrome && dragY === 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="max-w-md mx-auto flex items-center justify-between text-white/90 text-[14.5px] font-medium">
          {/* Reply Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              triggerHaptic('light');
              if (onReply) {
                onClose();
                onReply();
              }
            }}
            className="flex items-center gap-2 py-2 px-3 -ml-3 rounded-full hover:bg-white/10 active:opacity-60 transition-colors"
            aria-label="Reply"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.9" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
            </svg>
            {repliesCount > 0 && <span>{repliesCount}</span>}
          </button>

          {/* Repost / Re-bazz Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onReBazz) onReBazz(e);
            }}
            className={`flex items-center gap-2 py-2 px-3 rounded-full hover:bg-white/10 transition-colors active:scale-95 ${
              reBazzed ? 'text-emerald-400 font-bold' : ''
            }`}
            aria-label="Repost"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.9" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3" />
            </svg>
            {reBazzesCount > 0 && <span>{reBazzesCount}</span>}
          </button>

          {/* Like Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onLike) onLike(e);
            }}
            className={`flex items-center gap-2 py-2 px-3 rounded-full hover:bg-white/10 transition-colors active:scale-110 ${
              liked ? 'text-rose-500 font-bold' : ''
            }`}
            aria-label="Like"
          >
            <svg
              className={`w-5 h-5 transition-transform ${liked ? 'fill-current text-rose-500 scale-105' : ''}`}
              fill={liked ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="1.9"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
            </svg>
            {likesCount > 0 && <span>{likesCount}</span>}
          </button>

          {/* Share Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (onShare) onShare(e);
            }}
            className="flex items-center gap-2 py-2 px-3 -mr-3 rounded-full hover:bg-white/10 active:opacity-60 transition-colors"
            aria-label="Share"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.9" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" />
            </svg>
          </button>
        </div>
      </footer>
    </div>,
    document.body
  );
}
